import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import '../styles/ScholarLogin.css';
import { postRequest } from '../utilities/apicalls';
import { parseApiError, parseFormError } from '../utilities/errorHandler';
import { Form, Input, Button } from "antd";
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import ToastMessage from '../components/ToastMessage';

/**
 * ScholarLogin Component
 * 
 * Provides scholar login functionality with email verification
 * and options to verify as a student or login as an admin.
 */
const ScholarLogin = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const history = useHistory();

	// Configure message settings
	ToastMessage.config({
		top: 100,
		duration: 3,
		maxCount: 1
	});

	const handleVerifyClick = async () => {
		try {
			const values = await form.validateFields();
			const email = values.email;
			
			setLoading(true);
			
			// Create FormData object
			const formData = new FormData();
			formData.append('email', email);
			
			// Use FormData for the API call
			const response = await postRequest('scholars/verify_email', formData, true, true);

			if(response.status === 200) {
				// Store email for OTP verification
				localStorage.setItem('email', email);
				
				ToastMessage.success('Email verification successful');
				history.push('/scholar-otp');
			} else {
				// Handle non-200 response
				const errorMessage = response?.messages?.error_messages?.[0] || 'Please check your email and try again';
				ToastMessage.error(errorMessage);
			}
		} catch (error) {
			// console.error('Login error:', error);
			if (error.errorFields) {
				// Form validation errors
				ToastMessage.error(parseFormError(error.errorFields));
			} else {
				// API errors
				const errorMessage = error?.messages?.error_messages?.[0] || parseApiError(error);
				ToastMessage.error(errorMessage);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleAdminLoginClick = () => {
		history.push('/admin-login');
	};

	return (
		<div className="scholar-login">
			<main className="body">
				<div className="container">
					<div className="card">
						{/* Header Section */}
						<header className="header">
							<img 
								className="logo" 
								src={`${process.env.PUBLIC_URL}/images/Smart Forms Logo.png`} 
								alt="Smart Forms Logo" 
							/>
							<h2 className="student-portal">Scholar Portal</h2>
						</header>

						{/* Form Section */}
						<Form
							form={form}
							className="form"
							layout="vertical"
							validateTrigger="onSubmit"
						>
							<Form.Item
								name="email"
								validateFirst={true}
								rules={[
									{
										required: true,
										message: 'Please enter your email address'
									},
									{
										validator: async (_, value) => {
											if (!value) return;
											
											const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
											if (!emailPattern.test(value)) {
												throw new Error('Please enter a valid email address');
											}
										}
									}
								]}
							>
								<Input
									prefix={<UserOutlined />}
									placeholder="Your email address"
									size="large"
									className="email-input"
								/>
							</Form.Item>
							
							{/* Verify Button */}
							<Button 
								type="primary"
								className="verify-button" 
								onClick={handleVerifyClick}
								loading={loading}
								block
							>
								{loading ? 'Verifying...' : 'Verify'}
							</Button>
							
							<div className="divider">or</div>
							
							{/* Admin Login Button */}
							<Button 
								className="admin-button" 
								onClick={handleAdminLoginClick}
								block
								icon={<LockOutlined className="admin-icon" />}
							>
								<span className="admin-text">Login as Admin</span>
							</Button>
						</Form>
					</div>
					{/* Company Title */}
					<div className="company-info">
						<img 
							className="myt-logo" 
							src={`${process.env.PUBLIC_URL}/images/MYT Logo.png`} 
							alt="MYT Logo" 
						/>
						<span className="company-title">MYT Softdev Solutions Inc.</span>
					</div>
				</div>
			</main>
		</div>
	);
};

export default ScholarLogin; 